import { AvailableTemplate, ColumnType, DataTable, ExcelExportDto } from '@ecosystem/export-api';
import { DataColumn, ExportOptions, FormatHint, GenericWarning, KpiWithChangeColumn } from '../../types';

import { formatLabel, getExportContext, mapValueToDataCell } from '../../export';
import { Formatting } from '../../data/formatting';
import { RowDataSets, SeriesAndSummaryData, getNullFillerRow, getRowDataSets } from './data';
import { getGlobalWarningMessages } from '../utils';

export const chartDataToExcelData = (
  { fileHeaderSettings, fileName }: ExportOptions,
  chartData: SeriesAndSummaryData,
  xColumn: DataColumn,
  yColumn: DataColumn,
  currency: string,
  globalWarnings: GenericWarning[] = []
): ExcelExportDto => {
  const exportData: ExcelExportDto = {
    template: AvailableTemplate.Excel,
    name: fileName,
    worksheets: [
      {
        context: [...getExportContext(fileHeaderSettings ?? null), ...getGlobalWarningMessages(globalWarnings)],
        worksheetName: 'time series',
        data: createWorksheetData(chartData, xColumn, yColumn, currency),
      },
    ],
  };
  return exportData;
};

function createSummaryColumns(chartData: SeriesAndSummaryData) {
  return (chartData.summary?.dataLabels || []).map(summaryLabel => ({
    type: ColumnType.Text,
    header: (Array.isArray(summaryLabel) ? summaryLabel : [summaryLabel]).join('-'),
    style: { width: 20 },
  }));
}

function createSeriesColumns(chartData: SeriesAndSummaryData) {
  return (chartData.series.dataLabels || []).map(dataLabel => ({
    type: ColumnType.Text,
    header: formatLabel(dataLabel),
    style: {
      width: 17,
    },
  }));
}

const getFormatForTotalsRow = (yColumn: DataColumn): FormatHint | undefined => {
  if ([FormatHint.Percentage, FormatHint.PercentagePoints].includes(yColumn.formatHint)) {
    return undefined; // Totals row does not need to format in percentages
  }
  return yColumn.formatHint;
};

function createTotalsRow(chartData: SeriesAndSummaryData, yColumn: KpiWithChangeColumn) {
  return [
    {
      type: ColumnType.Text,
      value: yColumn.label,
    },
    {
      type: ColumnType.Text,
      value: 'Market',
    },
    ...(chartData.summary?.totalsDataSet?.values.raw.map(value =>
      mapValueToDataCell(value, getFormatForTotalsRow(yColumn), yColumn.dataType)
    ) || []),
    ...(chartData.series.totalsDataSet?.values.raw.map(value =>
      mapValueToDataCell(value, getFormatForTotalsRow(yColumn), yColumn.dataType)
    ) || []),
  ];
}
function createSeriesRows(
  rowDataSets: RowDataSets[],
  yColumnWithChange: KpiWithChangeColumn,
  summaryFiller: number[],
  seriesFiller: number[]
) {
  return rowDataSets.map(set => {
    return [
      {
        type: ColumnType.Text,
        value: yColumnWithChange.label,
      },
      {
        type: ColumnType.Text,
        value: set.label,
      },
      ...(set.summarySet?.values.raw || summaryFiller).map(value =>
        mapValueToDataCell(value, yColumnWithChange.formatHint, yColumnWithChange.dataType)
      ),
      ...(set.seriesSet.values.raw || seriesFiller).map(value =>
        mapValueToDataCell(value, yColumnWithChange.formatHint, yColumnWithChange.dataType)
      ),
    ];
  });
}

function createTotalChangesRow(chartData: SeriesAndSummaryData, yColumnWithChange: KpiWithChangeColumn) {
  return [
    {
      type: ColumnType.Text,
      value: yColumnWithChange.label + ' change',
    },
    {
      type: ColumnType.Text,
      value: 'Market',
    },
    ...(chartData.summary?.totalsDataSet?.changes?.raw.map(value =>
      mapValueToDataCell(value, getFormatForTotalsRow(yColumnWithChange), yColumnWithChange.dataType)
    ) || []),
    ...(chartData.series.totalsDataSet?.changes?.raw.map(value =>
      mapValueToDataCell(value, getFormatForTotalsRow(yColumnWithChange), yColumnWithChange.dataType)
    ) || []),
  ];
}

function createChangeSeriesRows(
  rowDataSets: RowDataSets[],
  yColumnWithChange: KpiWithChangeColumn,
  summaryFiller: number[],
  seriesFiller: number[]
) {
  return rowDataSets.map(set => {
    return [
      {
        type: ColumnType.Text,
        value: yColumnWithChange.label + ' change',
      },
      {
        type: ColumnType.Text,
        value: set.label,
      },
      ...(set.summarySet?.changes?.raw || summaryFiller).map(value =>
        mapValueToDataCell(value, yColumnWithChange.changeFormatHint, yColumnWithChange.dataType)
      ),
      ...(set.seriesSet?.changes?.raw || seriesFiller).map(value =>
        mapValueToDataCell(value, yColumnWithChange.changeFormatHint, yColumnWithChange.dataType)
      ),
    ];
  });
}

export const createWorksheetData = (
  chartData: SeriesAndSummaryData,
  xColumn: DataColumn,
  yColumn: DataColumn,
  currency: string
): DataTable => {
  const includeTotals = chartData.series.totalsDataSet !== undefined;
  const yColumnWithChange = yColumn as KpiWithChangeColumn;

  const rowDataSets = getRowDataSets(chartData);
  const summaryFiller = getNullFillerRow(chartData.summary);
  const seriesFiller = getNullFillerRow(chartData.series);

  return {
    currencySymbol: Formatting.getCurrencySymbol(currency) || currency,
    dataColumns: [
      { type: ColumnType.Text, header: 'KPI' },
      { type: ColumnType.Text, header: xColumn.label },

      ...(chartData.summary ? createSummaryColumns(chartData) : []),
      ...createSeriesColumns(chartData),
    ],
    dataRows: [
      includeTotals ? createTotalsRow(chartData, yColumnWithChange) : [],
      ...createSeriesRows(rowDataSets, yColumnWithChange, summaryFiller, seriesFiller),
      includeTotals ? createTotalChangesRow(chartData, yColumnWithChange) : [],
      ...createChangeSeriesRows(rowDataSets, yColumnWithChange, summaryFiller, seriesFiller),
    ].filter(Boolean),
  };
};
